import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Edit, Trash2, Upload, X, Eye, ChevronDown, Star } from 'lucide-react';
import { Combobox } from '@/components/Combobox';
import api from '@/lib/api';
import { toast } from 'sonner';
import BulkActionsToolbar from '@/components/BulkActionsToolbar';
import CustomCheckbox from '@/components/Checkbox';
import Wizard from '@/components/Wizard';
import Pagination from '@/components/Pagination';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/contexts/PermissionsContext';
import { ProtectedButton } from '@/components/rbac';

const Proprietaires = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [proprietaires, setProprietaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  const [formData, setFormData] = useState({ 
    id: '', 
    nom: '', 
    contact: '', 
    nom_contact_principal: '',
    telephone: '',
    telephone_2: '',
    pays: 'Côte d\'Ivoire',
    type_proprietaire: 'Personne physique',
    email: '',
    adresse: '',
    commentaire: '',
    types_camions: [],
    zone_couverture: [],
    service_specifique: '',
    // Nouveaux champs
    contacts: [
      { nom: '', role: '', email: '', telephone: '', is_principal: true, is_urgence: false }
    ],
    intra_inter_land: 'Intraland',
    is_clef: false
  });

  // Filtres avancés
  const [filters, setFilters] = useState({
    search: '',
    pays: '',
    type_proprietaire: '',
    type_camion: '',
    intra_inter_land: '',
    is_clef: 'all'
  });
  
  // Toggle advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Wizard state
  const [currentWizardStep, setCurrentWizardStep] = useState(0);
  
  // Wizard steps configuration
  const wizardSteps = [
    {
      title: "Informations générales",
      description: "Nom et type"
    },
    {
      title: "Contact & Localisation",
      description: "Coordonnées et adresse"
    },
    {
      title: "Flotte & Services",
      description: "Types de camions et zone de couverture"
    },
    {
      title: "Détails complémentaires",
      description: "Email, adresse et commentaire"
    }
  ];

  const paysAfriqueOuest = [
    'Bénin', 'Burkina Faso', 'Cap-Vert', 'Côte d\'Ivoire', 'Gambie', 'Ghana',
    'Guinée', 'Guinée-Bissau', 'Liberia', 'Mali', 'Mauritanie', 'Niger',
    'Nigeria', 'Sénégal', 'Sierra Leone', 'Togo'
  ];

  const [typesCamion, setTypesCamion] = useState([]);

  useEffect(() => {
    fetchProprietaires();
    fetchTypesCamion();
  }, []);

  const fetchTypesCamion = async () => {
    try {
      const response = await api.get('/types-camion');
      setTypesCamion(response.data || []);
    } catch (error) {
      console.error('Erreur chargement types camion:', error);
    }
  };

  const fetchProprietaires = async () => {
    try {
      const response = await api.get('/proprietaires');
      setProprietaires(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await api.put(`/proprietaires/${formData.id}`, formData);
        toast.success('Propriétaire modifié');
      } else {
        await api.post('/proprietaires', formData);
        toast.success('Propriétaire créé');
      }
      setDialogOpen(false);
      resetForm();
      fetchProprietaires();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce propriétaire ?')) return;
    try {
      await api.delete(`/proprietaires/${id}`);
      toast.success('Propriétaire supprimé');
      fetchProprietaires();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Bulk selection handlers
  const handleSelectAll = () => {
    // Sélectionner uniquement les éléments de la page actuelle
    const currentPageIds = paginatedProprietaires.map(p => p.id);
    setSelectedIds(currentPageIds);
  };

  const handleDeselectAll = () => {
    setSelectedIds([]);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  const handleBulkDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const confirmBulkDelete = async () => {
    try {
      await api.post('/proprietaires/bulk-delete', { ids: selectedIds });
      toast.success(`${selectedIds.length} partenaire(s) supprimé(s)`);
      setSelectedIds([]);
      setDeleteConfirmOpen(false);
      fetchProprietaires();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };


  const handleTogglePartenaireCle = async (id, currentValue) => {
    try {
      await api.put(`/proprietaires/${id}/cle`, { is_clef: !currentValue });
      toast.success(!currentValue ? 'Partenaire marqué comme clé' : 'Partenaire retiré des clés');
      fetchProprietaires();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const resetForm = () => {
    setFormData({ 
      id: '', 
      nom: '', 
      contact: '', 
      nom_contact_principal: '',
      telephone: '',
      telephone_2: '',
      pays: 'Côte d\'Ivoire',
      type_proprietaire: 'Personne physique',
      email: '',
      adresse: '',
      commentaire: '',
      types_camions: [],
      zone_couverture: [],
      service_specifique: '',
      contacts: [
        { nom: '', role: '', email: '', telephone: '', is_principal: true, is_urgence: false }
      ],
      intra_inter_land: 'Intraland',
      is_clef: false
    });
    setEditMode(false);
    setCurrentWizardStep(0);
  };
  
  // Wizard navigation handlers
  const handleWizardNext = () => {
    if (currentWizardStep < wizardSteps.length - 1) {
      setCurrentWizardStep(currentWizardStep + 1);
    }
  };

  const handleWizardPrev = () => {
    if (currentWizardStep > 0) {
      setCurrentWizardStep(currentWizardStep - 1);
    }
  };

  const canProceedToNextStep = () => {
    switch(currentWizardStep) {
      case 0: // Step 1: General info
        return formData.nom && formData.type_proprietaire;
      case 1: // Step 2: Contact
        return formData.contact && formData.pays;
      case 2: // Step 3: Flotte & Services (optional)
        return true;
      case 3: // Step 4: Details (optional)
        return true;
      default:
        return true;
    }
  };

  // Filtered proprietaires with memoization
  const filteredProprietaires = useMemo(() => {
    return proprietaires.filter(p => {
      // Filtrage par recherche (nom)
      if (filters.search && !p.nom.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }
      // Filtrage par pays
      if (filters.pays && p.pays !== filters.pays) {
        return false;
      }
      // Filtrage par type
      if (filters.type_proprietaire && p.type_proprietaire !== filters.type_proprietaire) {
        return false;
      }
      // Filtrage par type de camion possédé (par ID)
      if (filters.type_camion && filters.type_camion !== 'all') {
        const hasCamionType = p.camions_stats?.some(stat => {
          // Trouver le type de camion correspondant par nom
          const matchingType = typesCamion.find(t => t.id === filters.type_camion);
          return matchingType && stat.type_camion_nom === matchingType.type_camion;
        });
        if (!hasCamionType) return false;
      }
      // Filtrage par intra/interland
      if (filters.intra_inter_land && p.intra_inter_land !== filters.intra_inter_land) {
        return false;
      }
      // Filtrage par partenaire clé
      if (filters.is_clef === 'cles') {
        if (!p.is_clef) return false;
      } else if (filters.is_clef === 'non-cles') {
        if (p.is_clef) return false;
      }
      return true;
    });
  }, [proprietaires, filters, typesCamion]);

  // Pagination logic with useMemo
  const totalPages = useMemo(() => Math.ceil(filteredProprietaires.length / pageSize), [filteredProprietaires.length, pageSize]);
  const paginatedProprietaires = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredProprietaires.slice(startIndex, endIndex);
  }, [filteredProprietaires, currentPage, pageSize]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedIds([]); // Désélectionner lors du changement de page
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Retourner à la première page
    setSelectedIds([]);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);
  
  // Render wizard step content
  const renderWizardStepContent = () => {
    switch(currentWizardStep) {
      case 0: // Step 1: Informations générales
        return (
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="nom">Nom *</Label>
              <Input
                id="nom"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                placeholder="Ex: Transport Baobab SARL"
                required
              />
            </div>
            
            <div className="col-span-2 space-y-2">
              <Label htmlFor="type_proprietaire">Type *</Label>
              <Select
                value={formData.type_proprietaire}
                onValueChange={(value) => setFormData({ ...formData, type_proprietaire: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Personne physique">Personne physique</SelectItem>
                  <SelectItem value="Personne morale">Personne morale (Société)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
        
      case 1: // Step 2: Contact & Localisation
        return (
          <div className="space-y-6">
            {/* Système de 3 contacts */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-3">📞 Contacts (3 maximum)</h3>
              {(formData.contacts || []).map((contact, index) => (
                <div key={index} className={`p-4 mb-4 rounded-lg border ${index === 0 ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-800">
                      Contact {index + 1} {index === 0 && <span className="text-xs text-blue-600">(Obligatoire)</span>}
                    </h4>
                    {index > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newContacts = formData.contacts.filter((_, i) => i !== index);
                          setFormData({ ...formData, contacts: newContacts });
                        }}
                      >
                        <X className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Nom {index === 0 && '*'}</Label>
                      <Input
                        value={contact.nom || ''}
                        onChange={(e) => {
                          const newContacts = [...formData.contacts];
                          newContacts[index].nom = e.target.value;
                          setFormData({ ...formData, contacts: newContacts });
                        }}
                        placeholder="Ex: M. Kouamé"
                        required={index === 0}
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label>Rôle</Label>
                      <Input
                        value={contact.role || ''}
                        onChange={(e) => {
                          const newContacts = [...formData.contacts];
                          newContacts[index].role = e.target.value;
                          setFormData({ ...formData, contacts: newContacts });
                        }}
                        placeholder="Ex: Directeur"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={contact.email || ''}
                        onChange={(e) => {
                          const newContacts = [...formData.contacts];
                          newContacts[index].email = e.target.value;
                          setFormData({ ...formData, contacts: newContacts });
                        }}
                        placeholder="email@exemple.com"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label>Téléphone {index === 0 && '*'}</Label>
                      <Input
                        value={contact.telephone || ''}
                        onChange={(e) => {
                          const newContacts = [...formData.contacts];
                          newContacts[index].telephone = e.target.value;
                          setFormData({ ...formData, contacts: newContacts });
                        }}
                        placeholder="+225 XX XX XX XX XX"
                        required={index === 0}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={contact.is_principal || false}
                        onCheckedChange={(checked) => {
                          const newContacts = formData.contacts.map((c, i) => ({
                            ...c,
                            is_principal: i === index ? checked : false
                          }));
                          setFormData({ ...formData, contacts: newContacts });
                        }}
                      />
                      <Label>Contact Principal</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={contact.is_urgence || false}
                        onCheckedChange={(checked) => {
                          const newContacts = formData.contacts.map((c, i) => ({
                            ...c,
                            is_urgence: i === index ? checked : false
                          }));
                          setFormData({ ...formData, contacts: newContacts });
                        }}
                      />
                      <Label>Contact d'Urgence</Label>
                    </div>
                  </div>
                </div>
              ))}
              
              {formData.contacts.length < 3 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      contacts: [...formData.contacts, { nom: '', role: '', email: '', telephone: '', is_principal: false, is_urgence: false }]
                    });
                  }}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter un contact
                </Button>
              )}
            </div>
            
            {/* Pays et Type d'activité */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pays">Pays *</Label>
                <Select
                  value={formData.pays}
                  onValueChange={(value) => setFormData({ ...formData, pays: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paysAfriqueOuest.map((pays) => (
                      <SelectItem key={pays} value={pays}>{pays}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="intra_inter_land">Type d'activité</Label>
                <Select
                  value={formData.intra_inter_land || 'Intraland'}
                  onValueChange={(value) => setFormData({ ...formData, intra_inter_land: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Intraland">Intraland</SelectItem>
                    <SelectItem value="Interland">Interland</SelectItem>
                    <SelectItem value="Les deux (Intra-Inter)">Les deux (Intra-Inter)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );
        
      case 2: // Step 3: Flotte & Services
        return (
          <div className="space-y-6">
            {/* Types de camions et capacités */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Types de camions et capacités</h3>
              <div className="space-y-3">
                <Combobox
                  options={(typesCamion || []).map(type => ({
                    value: type.id,
                    label: type.type_camion || type.libelle
                  }))}
                  value=""
                  onValueChange={(value) => {
                    const selected = (typesCamion || []).find(t => t.id === value);
                    if (selected) {
                      setFormData({
                        ...formData,
                        types_camions: [...(formData.types_camions || []), { 
                          type_camion_id: value, 
                          capacite_tonnes: 0,
                          nombre_camions: 1
                        }]
                      });
                    }
                  }}
                  placeholder="Ajouter un type de camion"
                  searchPlaceholder="Rechercher..."
                  emptyText={(typesCamion || []).length === 0 ? "Chargement..." : "Aucun type trouvé"}
                />
                
                {(formData.types_camions || []).length > 0 && (
                  <div className="space-y-3">
                    <div className="text-sm text-gray-600 font-medium bg-gray-50 px-3 py-2 rounded-md">
                      {(formData.types_camions || []).length} type{(formData.types_camions || []).length > 1 ? 's' : ''} de camion{(formData.types_camions || []).length > 1 ? 's' : ''} ajouté{(formData.types_camions || []).length > 1 ? 's' : ''} 
                      {' - '}Total: {(formData.types_camions || []).reduce((sum, c) => sum + (c.nombre_camions || 1), 0)} camion{(formData.types_camions || []).reduce((sum, c) => sum + (c.nombre_camions || 1), 0) > 1 ? 's' : ''}
                    </div>
                    {(formData.types_camions || []).map((camion, index) => {
                      const type = (typesCamion || []).find(t => t.id === camion.type_camion_id);
                      return (
                        <div key={index} className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-800">{type?.type_camion || type?.libelle}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setFormData({
                                  ...formData,
                                  types_camions: (formData.types_camions || []).filter((_, i) => i !== index)
                                });
                              }}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-600">Capacité (T)</Label>
                              <Input
                                type="number"
                                step="0.5"
                                value={camion.capacite_tonnes || 0}
                                onChange={(e) => {
                                  const newCamions = [...(formData.types_camions || [])];
                                  newCamions[index].capacite_tonnes = parseFloat(e.target.value) || 0;
                                  setFormData({ ...formData, types_camions: newCamions });
                                }}
                                className="h-9"
                                placeholder="Ex: 10"
                              />
                            </div>
                            
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-600">Nombre de camions</Label>
                              <Input
                                type="number"
                                min="1"
                                value={camion.nombre_camions || 1}
                                onChange={(e) => {
                                  const newCamions = [...(formData.types_camions || [])];
                                  newCamions[index].nombre_camions = parseInt(e.target.value) || 1;
                                  setFormData({ ...formData, types_camions: newCamions });
                                }}
                                className="h-9"
                                placeholder="Ex: 5"
                              />
                            </div>
                          </div>
                          
                          <div className="mt-2 text-xs text-gray-600">
                            = <span className="font-semibold text-emerald-700">{camion.nombre_camions || 1} × {type?.type_camion || 'Type'} de {camion.capacite_tonnes || 0} T</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Zone de couverture */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900">Zone de couverture</h3>
              <div className="space-y-2">
                <Label htmlFor="zone_pays_select">Ajouter un pays</Label>
                <Select
                  onValueChange={(value) => {
                    if (!formData.zone_couverture.includes(value)) {
                      setFormData({
                        ...formData,
                        zone_couverture: [...formData.zone_couverture, value]
                      });
                    }
                  }}
                >
                  <SelectTrigger id="zone_pays_select">
                    <SelectValue placeholder="Sélectionnez un pays" />
                  </SelectTrigger>
                  <SelectContent>
                    {paysAfriqueOuest.map((pays) => (
                      <SelectItem key={pays} value={pays}>{pays}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Liste des pays ajoutés */}
                {formData.zone_couverture.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.zone_couverture.map((pays, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                        <span>{pays}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              zone_couverture: formData.zone_couverture.filter((_, i) => i !== idx)
                            });
                          }}
                          className="hover:text-green-900"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Service spécifique */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900">Service spécifique</h3>
              <div className="space-y-2">
                <Textarea
                  id="service_specifique"
                  value={formData.service_specifique || ''}
                  onChange={(e) => setFormData({ ...formData, service_specifique: e.target.value })}
                  placeholder="Ex: Transport de marchandises dangereuses..."
                  rows={4}
                />
              </div>
            </div>
          </div>
        );
        
      case 3: // Step 4: Détails complémentaires
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="contact@example.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="adresse">Adresse complète</Label>
              <Textarea
                id="adresse"
                value={formData.adresse}
                onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                placeholder="Adresse postale complète..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="commentaire">Commentaire / Notes</Label>
              <Textarea
                id="commentaire"
                value={formData.commentaire || ''}
                onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
                placeholder="Informations complémentaires, notes de prospection..."
                rows={3}
              />
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="p-8 space-y-6" data-testid="proprietaires-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Partenaires</h1>
          <p className="text-gray-600 mt-2">Gestion des partenaires et sociétés de transport</p>
        </div>
        {hasPermission('proprietaires', 'create') && (
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-proprietaire-btn">
            <Plus className="h-5 w-5 mr-2" />
            Ajouter un partenaire
          </Button>
        )}
      </div>

      {/* Search & Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Rechercher par nom..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10 h-11"
                data-testid="search-proprietaire-input"
              />
            </div>
          </div>
          
          {/* Bouton Filtres avancés */}
          <div className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="w-full"
            >
              {showAdvancedFilters ? '▲ Masquer les filtres avancés' : '▼ Afficher les filtres avancés'}
            </Button>
          </div>
          
          {/* Filtres avancés */}
          {showAdvancedFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-sm text-gray-700 mb-3">Filtres avancés</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Filtre Pays */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Pays</Label>
                  <Select
                    value={filters.pays || "all"}
                    onValueChange={(value) => setFilters({ ...filters, pays: value === "all" ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les pays</SelectItem>
                      {paysAfriqueOuest.map((pays) => (
                        <SelectItem key={pays} value={pays}>{pays}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Filtre Type de camion */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Type de camion</Label>
                  <Select
                    value={filters.type_camion || "all"}
                    onValueChange={(value) => setFilters({ ...filters, type_camion: value === "all" ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      {(typesCamion || []).map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.type_camion}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Filtre Type proprietaire */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Type de propriétaire</Label>
                  <Select
                    value={filters.type_proprietaire || "all"}
                    onValueChange={(value) => setFilters({ ...filters, type_proprietaire: value === "all" ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      <SelectItem value="Personne physique">Personne physique</SelectItem>
                      <SelectItem value="Personne morale">Personne morale</SelectItem>
                      <SelectItem value="Entreprise ou société de transport">Entreprise ou société de transport</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Filtre Inter/Intraland */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Inter/Intraland</Label>
                  <Select
                    value={filters.intra_inter_land || "all"}
                    onValueChange={(value) => setFilters({ ...filters, intra_inter_land: value === "all" ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="Interland">Interland</SelectItem>
                      <SelectItem value="Intraland">Intraland</SelectItem>
                      <SelectItem value="Les deux (Intra-Inter)">Les deux (Intra-Inter)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Filtre Partenaire clé */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Partenaire clé</Label>
                  <Select
                    value={filters.is_clef || "all"}
                    onValueChange={(value) => setFilters({ ...filters, is_clef: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="cles">⭐ Partenaires clés uniquement</SelectItem>
                      <SelectItem value="non-cles">Standards uniquement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {(filters.search || filters.pays || filters.type_proprietaire || filters.type_camion || filters.intra_inter_land || filters.is_clef !== 'all') && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setFilters({ search: '', pays: '', type_proprietaire: '', type_camion: '', intra_inter_land: '', is_clef: 'all' })}
                  className="mt-4"
                >
                  Réinitialiser les filtres
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle>
            Liste des partenaires ({proprietaires.filter(p => {
              if (filters.search && !p.nom.toLowerCase().includes(filters.search.toLowerCase())) return false;
              if (filters.pays && p.pays !== filters.pays) return false;
              if (filters.type_proprietaire && p.type_proprietaire !== filters.type_proprietaire) return false;
              return true;
            }).length} / {proprietaires.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Bulk Actions Toolbar */}
          <BulkActionsToolbar
            selectedCount={selectedIds.length}
            totalCount={proprietaires.length}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onBulkDelete={handleBulkDelete}
            entityName="partenaire"
          />

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 w-12">
                    <CustomCheckbox
                      checked={selectedIds.length === proprietaires.length && proprietaires.length > 0}
                      onChange={(checked) => checked ? handleSelectAll() : handleDeselectAll()}
                    />
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Matricule</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Nom</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Catégorie</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Types de Camion</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Contact(s)</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Activité</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Pays</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">⭐</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProprietaires.map((p) => (
                  <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <CustomCheckbox
                        checked={selectedIds.includes(p.id)}
                        onChange={() => handleToggleSelect(p.id)}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-mono text-xs text-gray-600">{p.matricule || '-'}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {p.is_clef && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />}
                        <span className="font-medium text-gray-900">{p.nom}</span>
                        {p.is_clef && <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 text-xs">⭐ CLÉ</Badge>}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded">
                        {p.type_proprietaire || 'N/A'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="space-y-1">
                        {p.camions_stats && p.camions_stats.length > 0 ? (
                          p.camions_stats.map((stat, idx) => (
                            <div key={idx} className="text-xs text-gray-700">
                              <span className="font-medium">{stat.type_camion_nom}</span>
                              <span className="text-gray-500"> ({stat.count}) - </span>
                              <span className="text-emerald-600 font-semibold">{stat.capacite_totale}T</span>
                            </div>
                          ))
                        ) : (
                          <span className="text-gray-400 text-xs italic">Aucun camion</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="space-y-1">
                        {p.contacts && p.contacts.length > 0 ? (
                          p.contacts.map((contact, idx) => (
                            <div key={idx} className="text-sm">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-700">{contact.nom || '-'}</span>
                                {contact.is_principal && (
                                  <Badge className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0">Principal</Badge>
                                )}
                                {contact.is_urgence && (
                                  <Badge className="bg-red-100 text-red-700 text-xs px-1.5 py-0">Urgence</Badge>
                                )}
                              </div>
                              {contact.role && <div className="text-xs text-gray-500">{contact.role}</div>}
                              {contact.telephone && <div className="text-xs text-gray-600">{contact.telephone}</div>}
                            </div>
                          ))
                        ) : (
                          // Fallback pour anciens données sans structure contacts
                          <div className="space-y-1">
                            {(p.telephone || p.contact) && (
                              <div className="text-gray-700 text-sm">
                                {p.telephone || p.contact}
                              </div>
                            )}
                            {p.telephone_2 && (
                              <div className="text-gray-600 text-xs">
                                {p.telephone_2}
                              </div>
                            )}
                            {!p.telephone && !p.contact && !p.telephone_2 && (
                              <span className="text-gray-400 text-sm italic">-</span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-xs text-gray-700">{p.intra_inter_land || 'Intraland'}</span>
                    </td>
                    <td className="py-3 px-4 text-gray-700">{p.pays}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center">
                        <Switch
                          checked={p.is_clef || false}
                          onCheckedChange={() => handleTogglePartenaireCle(p.id, p.is_clef)}
                          className="data-[state=checked]:bg-yellow-500"
                        />
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => { setFormData(p); setViewDetailsOpen(true); }} data-testid={`view-proprio-${p.id}`} title="Voir détails">
                          <Eye className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => { setFormData(p); setEditMode(true); setDialogOpen(true); }} data-testid={`edit-proprio-${p.id}`} title="Modifier">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)} data-testid={`delete-proprio-${p.id}`} title="Supprimer">
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredProprietaires.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filteredProprietaires.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer {selectedIds.length} partenaire(s) ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={confirmBulkDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) setCurrentWizardStep(0); // Reset wizard when closing
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="proprietaire-dialog">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>{editMode ? 'Modifier' : 'Ajouter'} un partenaire</DialogTitle>
              <DialogDescription>
                Remplissez les informations du partenaire en suivant les étapes
              </DialogDescription>
            </DialogHeader>
            
            <Wizard
              steps={wizardSteps}
              currentStep={currentWizardStep}
              onNext={handleWizardNext}
              onPrev={handleWizardPrev}
              onSubmit={handleSubmit}
              canProceed={canProceedToNextStep()}
            >
              {renderWizardStepContent()}
            </Wizard>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modale Détails Partenaire */}
      <Dialog open={viewDetailsOpen} onOpenChange={setViewDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détails du Partenaire</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Informations générales */}
            <div className="border-b pb-4">
              <h3 className="font-semibold text-lg mb-3">Informations générales</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="font-medium">Matricule:</span> {formData.matricule || '-'}</div>
                <div><span className="font-medium">Nom:</span> {formData.nom || '-'}</div>
                <div><span className="font-medium">Type:</span> {formData.type_proprietaire || '-'}</div>
                <div><span className="font-medium">Pays:</span> {formData.pays || '-'}</div>
              </div>
            </div>

            {/* Contact */}
            <div className="border-b pb-4">
              <h3 className="font-semibold text-lg mb-3">Contact</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="font-medium">Contact principal:</span> {formData.nom_contact_principal || '-'}</div>
                <div><span className="font-medium">Email:</span> {formData.email || '-'}</div>
                <div><span className="font-medium">Téléphone 1:</span> {formData.telephone || '-'}</div>
                <div><span className="font-medium">Téléphone 2:</span> {formData.telephone_2 || '-'}</div>
                <div className="col-span-2"><span className="font-medium">Adresse:</span> {formData.adresse || '-'}</div>
              </div>
            </div>

            {/* Flotte & Services */}
            {((formData.types_camions && formData.types_camions.length > 0) || 
              (formData.zone_couverture && formData.zone_couverture.length > 0) || 
              formData.service_specifique) && (
              <div className="border-b pb-4">
                <h3 className="font-semibold text-lg mb-3">Flotte & Services</h3>
                
                {formData.types_camions && formData.types_camions.length > 0 && (
                  <div className="mb-3">
                    <p className="font-medium text-sm mb-2">Types de camions:</p>
                    <div className="space-y-2">
                      {formData.types_camions.map((camion, idx) => {
                        const type = (typesCamion || []).find(t => t.id === camion.type_camion_id);
                        return (
                          <div key={idx} className="bg-blue-50 p-2 rounded text-sm">
                            <span className="font-medium">{type?.type_camion || type?.libelle || 'Type inconnu'}</span>
                            {' - '}
                            <span>{camion.nombre_camions || 1} camion(s) × {camion.capacite_tonnes || 0} T</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {formData.zone_couverture && formData.zone_couverture.length > 0 && (
                  <div className="mb-3">
                    <p className="font-medium text-sm mb-2">Zone de couverture:</p>
                    <div className="flex flex-wrap gap-2">
                      {formData.zone_couverture.map((pays, idx) => (
                        <span key={idx} className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                          {pays}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {formData.service_specifique && (
                  <div>
                    <p className="font-medium text-sm mb-2">Service spécifique:</p>
                    <p className="text-sm text-gray-700">{formData.service_specifique}</p>
                  </div>
                )}
              </div>
            )}

            {/* Commentaire */}
            {formData.commentaire && (
              <div>
                <h3 className="font-semibold text-lg mb-3">Commentaire</h3>
                <p className="text-sm text-gray-700">{formData.commentaire}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Proprietaires;
